import React, { useCallback, useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { localizeChineseText } from '../services/chineseText';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

const DOMAIN_KEYS = ['家庭', '財富', '婚姻', '事業', '健康'];

const positionLabelKey = {
  previous: 'previousLunarYear',
  current: 'currentLunarYear',
  next: 'nextLunarYear'
};

const AnnualFortunePage = ({ data, t, lang = 'zh-TW' }) => {
  const years = data?.annual_fortunes?.years || [];
  const [selectedPosition, setSelectedPosition] = useState(years[1]?.position || years[0]?.position || 'current');
  const selectedYear = years.find((year) => year.position === selectedPosition) || years[0];
  const txt = useCallback((value) => localizeChineseText(value, lang), [lang]);

  const chartData = useMemo(() => ({
    labels: selectedYear?.months?.map((month) => txt(`${month.label}${month.branch}`)) || [],
    datasets: [
      {
        label: t('fortuneScore'),
        data: selectedYear?.months?.map((month) => month.score) || [],
        borderColor: '#b86a12',
        backgroundColor: 'rgba(184, 106, 18, 0.16)',
        pointBackgroundColor: '#b86a12',
        pointBorderColor: '#fffaf0',
        pointRadius: 4,
        borderWidth: 2,
        tension: 0.35,
        fill: true
      }
    ]
  }), [selectedYear, t, txt]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => {
            const month = selectedYear?.months?.[context.dataIndex];
            return `${t('fortuneScore')}：${context.parsed.y} · ${txt(month?.tone || '')}`;
          },
          afterLabel: (context) => {
            const month = selectedYear?.months?.[context.dataIndex];
            return month?.relations?.length ? `${t('relationship')}：${txt(month.relations.join('、'))}` : '';
          }
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#8a857d' }
      },
      y: {
        min: 10,
        max: 100,
        ticks: { color: '#8a857d' },
        grid: { color: 'rgba(138, 133, 125, 0.18)' }
      }
    }
  }), [selectedYear, t, txt]);

  if (!selectedYear) return null;

  return (
    <div className="bz-annual-page">
      <section className="bz-intro bz-intro--compact fc-rise">
        <a className="fc-btn fc-btn--ghost fc-btn--sm" href="/bazi/">{t('backToIntegrated')}</a>
        <h1 className="bz-intro__title">{t('annualPageTitle')}</h1>
        <p className="bz-intro__desc">{t('annualPageDesc')}</p>
      </section>

      <div className="bz-tabs tabs tabs-border" role="tablist" aria-label={t('annualYearTabsLabel')}>
        {years.map((year) => (
          <button
            type="button"
            key={year.position}
            className={`bz-tab tab ${selectedPosition === year.position ? 'tab-active' : ''}`}
            role="tab"
            aria-selected={selectedPosition === year.position}
            onClick={() => setSelectedPosition(year.position)}
          >
            {t(positionLabelKey[year.position] || 'currentLunarYear')} · {year.year}
          </button>
        ))}
      </div>

      <section className="bz-block">
        <div className="fc-card bz-annual-hero">
          <div>
            <span className="fc-eyebrow">{t('taiSui')}</span>
            <h2>{selectedYear.year} · {txt(selectedYear.pillar)} · {txt(selectedYear.zodiac)}</h2>
            <p>{txt(selectedYear.overall)}</p>
          </div>
          <div className="bz-annual-score" aria-label={`${t('fortuneScore')} ${selectedYear.score}`}>
            <strong>{selectedYear.score}</strong>
            <span>{selectedYear.tone}</span>
          </div>
        </div>
      </section>

      <section className="bz-block">
        <h3 className="bz-section-title">{t('overallFortune')}</h3>
        <div className="fc-card bz-annual-narrative">
          <span className="fc-eyebrow">{t('annualOverallNarrativeTitle')}</span>
          <p>{txt(selectedYear.overall_narrative || selectedYear.overall)}</p>
        </div>
        <div className="bz-grid-3">
          <div className="fc-card">
            <span className="fc-eyebrow">{t('relationship')}</span>
            <p>{txt(selectedYear.relations?.join('、'))}</p>
          </div>
          <div className="fc-card">
            <span className="fc-eyebrow">{t('luckyStars')}</span>
            <p>{txt(selectedYear.lucky_stars?.join('、'))}</p>
          </div>
          <div className="fc-card">
            <span className="fc-eyebrow">{t('unluckyStars')}</span>
            <p>{txt(selectedYear.unlucky_stars?.join('、'))}</p>
          </div>
        </div>
      </section>

      <section className="bz-block">
        <h3 className="bz-section-title">{t('categoryFortune')}</h3>
        <div className="bz-fate-grid">
          {DOMAIN_KEYS.map((domain) => {
            const item = selectedYear.domains?.[domain];
            return (
              <article className="bz-fate-card" key={domain}>
                <h4>{txt(domain)}</h4>
                <span className="fc-tag fc-tag--accent">{txt(item?.tone)} · {item?.score}</span>
                <p>{txt(item?.text)}</p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="bz-block">
        <h3 className="bz-section-title">{t('monthFortuneChart')}</h3>
        <div className="fc-card">
          <div className="bz-chart bz-chart--line">
            <Line data={chartData} options={chartOptions} />
          </div>
        </div>
      </section>

      <section className="bz-block">
        <h3 className="bz-section-title">{t('monthlyDevelopment')}</h3>
        <p className="bz-note">{t('monthlyScrollHint')}</p>
        <div className="bz-calendar-tags" aria-label={t('calendarBasis')}>
          <span className="fc-tag fc-tag--accent">{t('lunarCalendarLabel')}</span>
          <span className="fc-tag">{t('gregorianInputLabel')}</span>
        </div>
        <div className="bz-month-scroll" tabIndex="0">
          {selectedYear.months?.map((month) => (
            <article className="bz-month-card" key={`${selectedYear.year}-${month.month}`}>
              <div className="bz-month-card__head">
                <span>
                  <span className="bz-month-card__title">{txt(month.label)}{txt(month.branch)}</span>
                  <span className="bz-month-card__calendar">{t('lunarCalendarLabel')}</span>
                </span>
                <span className="fc-tag">{txt(month.tone)} · {month.score}</span>
              </div>
              <p className="bz-note">{t('relationship')}：{txt(month.relations?.join('、'))}</p>
              {DOMAIN_KEYS.map((domain) => (
                <div className="bz-month-card__domain" key={domain}>
                  <strong>{txt(domain)}</strong>
                  <p>{txt(month.specific_development?.[domain])}</p>
                </div>
              ))}
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default AnnualFortunePage;
